// src/pages/admin/ReportsPage.tsx
import React, { useEffect, useState } from 'react';
import fetchWithAuth from '../../utils/fetchWithAuth';

type Report = {
  id: string;
  type: 'RECIPE' | 'USER';
  reason: string;
  description: string | null;
  status: 'PENDING' | 'REVIEWED' | 'RESOLVED' | 'DISMISSED';
  userId?: string;
  recipeId?: string;
};

export default function ReportsPage() {
  const [filterType, setFilterType] = useState<'RECIPE' | 'USER' | ''>('');
  const [reports, setReports] = useState<Report[]>([]);

  // Load reports whenever filterType changes
  useEffect(() => {
    (async () => {
      try {
        const url = `/api/admin/reports${filterType ? `?type=${filterType}` : ''}`;
        const data = await fetchWithAuth(url);
        setReports(data);
      } catch (err) {
        console.error('Failed to load reports', err);
      }
    })();
  }, [filterType]);

  const updateStatus = async (id: string, newStatus: Report['status']) => {
    try {
      await fetchWithAuth(`/api/admin/reports/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      // reload after update
      const url = `/api/admin/reports${filterType ? `?type=${filterType}` : ''}`;
      const data = await fetchWithAuth(url);
      setReports(data);
    } catch (err) {
      console.error('Failed to update report status', err);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">Reports</h1>

      <div className="mb-4">
        <label className="mr-2">Filter by type:</label>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as any)}
          className="p-2 border rounded"
        >
          <option value="">All</option>
          <option value="RECIPE">Recipe Reports</option>
          <option value="USER">User Reports</option>
        </select>
      </div>

      <table className="w-full table-auto border-collapse">
        <thead>
          <tr className="bg-gray-200">
            <th className="p-2 border">ID</th>
            <th className="p-2 border">Type</th>
            <th className="p-2 border">Target ID</th>
            <th className="p-2 border">Reason</th>
            <th className="p-2 border">Description</th>
            <th className="p-2 border">Status</th>
            <th className="p-2 border">Action</th>
          </tr>
        </thead>
        <tbody>
          {reports.map((r) => (
            <tr key={r.id} className="even:bg-gray-50">
              <td className="p-2 border">{r.id}</td>
              <td className="p-2 border">{r.type}</td>
              <td className="p-2 border">
                {r.type === 'RECIPE' ? r.recipeId : r.userId}
              </td>
              <td className="p-2 border">{r.reason}</td>
              <td className="p-2 border">{r.description || '-'}</td>
              <td className="p-2 border">{r.status}</td>
              <td className="p-2 border">
                <select
                  value={r.status}
                  onChange={(e) => updateStatus(r.id, e.target.value as any)}
                  className="p-1 border rounded"
                >
                  <option value="PENDING">PENDING</option>
                  <option value="REVIEWED">REVIEWED</option>
                  <option value="RESOLVED">RESOLVED</option>
                  <option value="DISMISSED">DISMISSED</option>
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
